/**
 * Decides whether an STT transcript is worth sending through LLM correction
 * (services/stt/correctTranscript.js) before it is dispatched.
 *
 * Polish used to run on every non-command utterance, which put a model round trip in
 * front of "yes" — latency on the turn, and one of the 2-3 ':stt' bucket calls a spoken
 * turn already spends. Modern STT returns punctuated, capitalized text on its own, so
 * correction earns its keep on fillers, run-ons and homophones, not on a two-word answer.
 *
 * Pure and exported for tests, the same way isValidCorrection is in correctTranscript.js.
 */

/** Above this many words, assume enough was said for correction to find something. */
const LONG_UTTERANCE_WORDS = 12;
/** At or below this, there is too little text for correction to improve anything. */
const TRIVIAL_WORDS = 3;

// Spoken fillers are the single thing correction most reliably removes. Held as sets and
// small patterns rather than one alternation, which trips the regex-complexity gate.
const FILLER_SOUND = /^(?:u+m+|u+h+|e+rm+|h+m+)$/;
const FILLER_WORDS = new Set(['like']);
const FILLER_PHRASES = ['you know', 'i mean', 'sort of', 'kind of'];

/** Answers that are already exactly what the user meant — matching, not prose. */
const SHORT_ANSWERS = new Set([
  'yes', 'yeah', 'yep', 'no', 'nope', 'okay', 'ok', 'sure', 'thanks', 'thank you',
  'stop', 'cancel', 'continue', 'correct', 'right', 'wrong',
]);
const OPTION_ANSWER = /^option\s+\w+$/;
const NUMERIC_ANSWER = /^[\d.,\s]+$/;

/** Lowercased words with surrounding punctuation stripped, so "Um," still reads as "um". */
function wordsOf(text) {
  return text.toLowerCase().split(/\s+/).map((w) => w.replace(/[^a-z0-9']/g, '')).filter(Boolean);
}

function hasFiller(lower, words) {
  if (words.some((w) => FILLER_WORDS.has(w) || FILLER_SOUND.test(w))) return true;
  return FILLER_PHRASES.some((phrase) => lower.includes(phrase));
}

const TERMINAL_PUNCTUATION = '.!?';

function isShortAnswer(text) {
  // Trimmed by hand rather than with a `[.!?]+$` regex, which backtracks super-linearly.
  let bare = text.trim().toLowerCase();
  while (bare.length && TERMINAL_PUNCTUATION.includes(bare[bare.length - 1])) {
    bare = bare.slice(0, -1);
  }
  bare = bare.trim();
  return SHORT_ANSWERS.has(bare) || OPTION_ANSWER.test(bare) || NUMERIC_ANSWER.test(bare);
}

/** Reads as a finished sentence: starts with a capital, ends with terminal punctuation. */
function looksClean(text) {
  return /^["'(]?[A-Z0-9]/.test(text) && /[.!?]["')]?$/.test(text);
}

/**
 * @param {string} text — the raw STT transcript
 * @returns {boolean} true when the transcript should be sent for LLM correction
 */
export function needsPolish(text) {
  const t = (text || '').trim();
  if (!t) return false;

  const words = wordsOf(t);

  // A filler outranks every "looks fine" signal below — "Yes, um, do it." is clean by
  // punctuation and short by word count, and still wants the filler gone.
  if (hasFiller(t.toLowerCase(), words)) return true;

  if (isShortAnswer(t)) return false;
  if (words.length <= TRIVIAL_WORDS) return false;

  // Already a tidy sentence of ordinary length — correction would be a no-op round trip.
  if (words.length <= LONG_UTTERANCE_WORDS && looksClean(t)) return false;

  // Long, or unpunctuated: where run-ons and homophones actually live.
  return true;
}

export default needsPolish;
